import { QueryCompositeFilterConstraint, QueryConstraint, collection, doc, onSnapshot, query } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react"
import { useFirestore } from "reactfire";
import { fromFirebaseDoc, fromFirebaseDocs } from "../utils/shared/firebase";

export const useFirebaseDoc = <T>(path: string | null | undefined): [T | null, Function, boolean] => {
    const [state, setState] = useState<{ document: T | null, loaded: boolean }>({ document: null, loaded: false })
    const firestore = useFirestore();

    useEffect(() => {
        if (!path) return;

        const pathParts = path.split('/');
        if (pathParts.length % 2 !== 0 || !pathParts[pathParts.length - 1]) {
            return;
        }

        return onSnapshot(doc(firestore, path), snap => {
            const result = fromFirebaseDoc<T>(snap);
            setState({ document: result, loaded: true })
        })
    }, [path, firestore])

    return [
        state.document,
        (newDocument: T) => setState({ document: newDocument, loaded: true }),
        state.loaded
    ]
}

export const useFirebaseQuery = <T>(path: string, ...queryConstraints: QueryConstraint[]): [T[], Function, boolean] => {
    return useFirebaseQueryOptions<T>(path, { queryConstraints });
}

export interface QueryOptions {
    queryConstraints: (QueryConstraint[] | QueryCompositeFilterConstraint),
    dependencies?: any[],
    skip?: boolean
}
export const useFirebaseQueryOptions = <T>(path: string, options: QueryOptions): [T[], Function, boolean] => {
    const [state, setState] = useState<{ documents: T[], loaded: boolean }>({ documents: [], loaded: false })
    const firestore = useFirestore();

    const serializedConstaints = useMemo(() => JSON.stringify(options.queryConstraints), [options.queryConstraints]);

    useEffect(() => {
        if (options.skip) {
            return;
        }
        let q;
        if (Array.isArray(options.queryConstraints)) {
            q = query(collection(firestore, path), ...options.queryConstraints)
        } else {
            q = query(collection(firestore, path), options.queryConstraints)
        }
        return onSnapshot(q, snap => {
            const result = fromFirebaseDocs<T>(snap.docs);
            setState({ documents: result, loaded: true })
        });
        // eslint-disable-next-line
    }, [path, firestore,
        serializedConstaints,
        options.skip,
        // eslint-disable-next-line 
        ...(options.dependencies || [])
    ]);

    return [
        state.documents,
        (newDocuments: T[]) => setState({ documents: newDocuments, loaded: true }),
        state.loaded]
}

